import { useState, useEffect } from "react";
import type { SignedSnapshot } from "../../client-messages/snapshots";

const MAX_CACHE_SIZE = 200;
const blobCache = new Map<string, string>();
const inflight = new Map<string, Promise<string>>();

function evictOldest() {
  while (blobCache.size > MAX_CACHE_SIZE) {
    const firstKey = blobCache.keys().next().value as string;
    blobCache.delete(firstKey);
  }
}

function fetchBlob(cacheKey: string, headers: Record<string, string>): Promise<string> {
  const existing = inflight.get(cacheKey);
  if (existing) return existing;

  const promise = fetch(cacheKey, { headers })
    .then((res) => {
      if (!res.ok) throw new Error(`HTTP ${String(res.status)}`);
      return res.blob();
    })
    .then((blob) => {
      const blobUrl = URL.createObjectURL(blob);
      blobCache.set(cacheKey, blobUrl);
      evictOldest();
      return blobUrl;
    })
    .finally(() => {
      inflight.delete(cacheKey);
    });

  inflight.set(cacheKey, promise);
  return promise;
}

export function useSignedUrl(snapshot: SignedSnapshot | null | undefined, width?: number | null): string | null {
  const cacheKey = snapshot ? snapshot.url + (width ? `?width=${width.toFixed()}` : "") : null;
  const [src, setSrc] = useState<string | null>(() => (cacheKey ? (blobCache.get(cacheKey) ?? null) : null));

  useEffect(() => {
    if (!snapshot || !cacheKey) {
      setSrc(null);
      return;
    }
    const cached = blobCache.get(cacheKey);
    if (cached) {
      setSrc(cached);
      return;
    }
    let cancelled = false;
    fetchBlob(cacheKey, snapshot.headers as Record<string, string>)
      .then((blobUrl) => {
        if (!cancelled) setSrc(blobUrl);
      })
      .catch((err: unknown) => {
        if (!cancelled && err instanceof Error) {
          setSrc(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [cacheKey, snapshot]);

  return src;
}
